import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { MissionDetail, TeamDetail, PlayerDetail } from '../types/gameDetail';
import { missionDetailService } from '../services/missionDetailService';
import { useAuth } from '../contexts/AuthContext';
import { useFieldAuth } from '../contexts/FieldContext';
import { useRefereeAuth } from '../contexts/RefereeContext';
import ReviewSummary from '../components/common/ReviewSummary';
import ReviewModal from '../components/common/ReviewModal';
import reviewService from '../services/reviewService';
import { ReviewSummary as ReviewSummaryType, Review } from '../types/review';
import ModernBackground from '../components/common/ModernBackground';
import MissionMapViewer from '../components/mission/MissionMapViewer';
import DeviceEventTimeline from '../components/mission/DeviceEventTimeline';
import CompletedObjectivesList from '../components/mission/CompletedObjectivesList';
import UncompletedObjectivesList from '../components/mission/UncompletedObjectivesList';
import InfoButton from '../components/common/InfoButton';

const GameDetailPage: React.FC = () => {
  const { gameType, gameId } = useParams<{ gameType: string; gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { field } = useFieldAuth();
  const { referee } = useRefereeAuth();
  const theme = useTheme() as any;
  const showModernBackground = theme.name === 'modernWarfare';
  
  const [gameDetail, setGameDetail] = useState<MissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Review states
  const [reviewSummary, setReviewSummary] = useState<ReviewSummaryType | null>(null);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    const fetchGameDetail = async () => {
      if (!gameId || !gameType) {
        setError('Invalid game parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let detail: MissionDetail;
        
        if (gameType === 'airsoft') {
          detail = await missionDetailService.getAirsoftMissionDetail(gameId);
        } else if (gameType === 'paintball') {
          detail = await missionDetailService.getPaintballMissionDetail(gameId);
        } else {
          throw new Error('Unsupported game type');
        }
        
        console.log('GameDetail operationId:', detail.operationId, 'operationName:', detail.operationName);
        setGameDetail(detail);
      } catch (error) {
        console.error('Error loading game details:', error);
        setError('Error loading game information');
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetail();
  }, [gameId, gameType]);


  // Загрузка отзывов
  const loadReviews = async () => {
    if (!gameId) return;
    
    setReviewsLoading(true);
    try {
      const [summaryData, myReviewData] = await Promise.all([
        reviewService.getGameReviewSummary(gameId),
        user ? reviewService.getMyReview('game', gameId) : Promise.resolve(null)
      ]);

      setReviewSummary(summaryData);
      setMyReview(myReviewData);
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Загружаем отзывы когда игра загрузилась
  useEffect(() => {
    if (gameDetail && gameId) {
      loadReviews();
    }
  }, [gameDetail, gameId, user]);

  // Обработчик отправки отзыва
  const handleReviewSubmitted = () => {
    loadReviews(); // Перезагружаем отзывы
    setIsReviewModalOpen(false);
  };

  // Форматируем дату без конвертации UTC - показываем локальное время поля
  const formatDate = (dateString: string) => {
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return dateString;

    const [, year, month, day, hours, minutes] = match;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[parseInt(month, 10) - 1];

    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 < 12 ? 'AM' : 'PM';

    return `${monthName} ${parseInt(day, 10)}, ${year}, ${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };


  if (loading) {
    return (
      <Background>
        {showModernBackground && <ModernBackground />}
        <Container>
          <Content>
            <LoadingSpinner>Loading...</LoadingSpinner>
          </Content>
        </Container>
      </Background>
    );
  }

  if (error || !gameDetail) {
    return (
      <Background>
        {showModernBackground && <ModernBackground />}
        <Container>
          <Content>
            <ErrorMessage>
              {error || 'Game not found'}
              <BackButton onClick={() => navigate(-1)}>
                Back
              </BackButton>
            </ErrorMessage>
          </Content>
        </Container>
      </Background>
    );
  }

  return (
    <Background>
      {showModernBackground && <ModernBackground />}
      <Container>
        <Content>
        <Header>
        <BackButton onClick={() => navigate(-1)}>
          ← Back
        </BackButton>
        <GameTitle>
          {gameDetail.gameType.toUpperCase()} - {gameDetail.uniqGameId}
        </GameTitle>
      </Header>

      <GameInfo>
        <InfoSection>
          <InfoTitle>Mission Details</InfoTitle>
          <InfoGrid>
            {gameDetail.operationName && (
              <InfoItem>
                <InfoLabel>Operation:</InfoLabel>
                {gameDetail.operationId ? (
                  <ClickableInfoValue
                    onClick={() => navigate(`/operations/${gameDetail.operationId}`)}
                    style={{ color: '#a855f7' }}
                  >
                    {gameDetail.operationName}
                  </ClickableInfoValue>
                ) : (
                  <InfoValue style={{ color: '#a855f7' }}>
                    {gameDetail.operationName}
                  </InfoValue>
                )}
              </InfoItem>
            )}
            <InfoItem>
              <InfoLabel>Field:</InfoLabel>
              <ClickableInfoValue onClick={() => navigate(`/fields/${gameDetail.fieldId}`)}>
                {gameDetail.fieldName}
              </ClickableInfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Referee:</InfoLabel>
              <ClickableInfoValue onClick={() => navigate(`/referee/public/${gameDetail.refereeId}`)}>
                {gameDetail.refereeName}
              </ClickableInfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Date:</InfoLabel>
              <InfoValue>{formatDate(gameDetail.missionDate)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Duration:</InfoLabel>
              <InfoValue>{formatDuration(gameDetail.gameDuration)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Result:</InfoLabel>
              <ResultBadge result={gameDetail.teamThatWon}>
                {gameDetail.teamThatWon === 'draw' ? 'Draw' : 
                 gameDetail.teamThatWon === 'human' ? 'Human Victory' : 
                 gameDetail.teamThatWon === 'alien' ? 'Alien Victory' : 
                 gameDetail.teamThatWon}
              </ResultBadge>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Ranked:</InfoLabel>
              <InfoValue>{gameDetail.isRanked ? 'Yes' : 'No'}</InfoValue>
            </InfoItem>
          </InfoGrid>
          {gameDetail.notes && (
            <NotesSection>
              <InfoLabel>Referee Notes:</InfoLabel>
              <NotesText>{gameDetail.notes}</NotesText>
            </NotesSection>
          )}
        </InfoSection>

        <PointsSection>
          <InfoTitle>Points</InfoTitle>
          <PointsGrid>
            <PointsItem>
              <PointsLabel>For Victory:</PointsLabel>
              <PointsValue>{gameDetail.pointsEarnedForWinners}</PointsValue>
            </PointsItem>
            <PointsItem>
              <PointsLabel>For Defeat:</PointsLabel>
              <PointsValue>{gameDetail.pointsEarnedForLosers}</PointsValue>
            </PointsItem>
          </PointsGrid>
        </PointsSection>
      </GameInfo>

      <TeamsSection>
        <SectionTitleRow>
          <SectionTitle>Teams</SectionTitle>
          <InfoButton
            title="Teams"
            sections={[
              {
                title: 'About Teams',
                description: 'This section shows the two teams that participated in the mission - Humans and Aliens.'
              },
              {
                title: 'Team Information',
                fields: [
                  { name: 'Faction', description: 'The team\'s faction - either Humans (blue) or Aliens (purple)' },
                  { name: 'Result', description: 'Whether the team won (Victory) or lost (Defeat) the mission' },
                  { name: 'Team Leader', description: 'The player designated as the team leader for this mission' },
                  { name: 'Players', description: 'List of all players on the team with their earned points' }
                ]
              }
            ]}
          />
        </SectionTitleRow>
        <TeamsGrid>
          {gameDetail.teams.map((team) => (
            <TeamCard key={team.id} isWinner={team.isWin}>
              <TeamHeader faction={team.faction}>
                <FactionBadge faction={team.faction}>
                  {team.faction === 'human' ? 'Human' : 'Alien'}
                </FactionBadge>
                <TeamResult isWinner={team.isWin}>
                  {team.isWin ? 'VICTORY' : 'DEFEAT'}
                </TeamResult>
              </TeamHeader>
              <TeamStats>
                Players: {team.playersCount}
                {team.teamLeader && (
                  <TeamLeader 
                    onClick={team.teamLeader.userId ? () => navigate(`/profile/${team.teamLeader!.userId}`) : undefined}
                    isClickable={!!team.teamLeader.userId}
                  >
                    Leader: {team.teamLeader.username}
                  </TeamLeader>
                )}
              </TeamStats>
              <PlayersList>
                {team.players.map((player, index) => (
                  <PlayerItem
                    key={player.userId || `unknown-${team.id}-${index}`}
                    onClick={player.userId ? () => navigate(`/profile/${player.userId}`) : undefined}
                    isClickable={!!player.userId}
                  >
                    <PlayerName>{player.username}</PlayerName>
                    <PlayerPointsWrapper>
                      <PlayerPoints>+{player.pointsEarned}</PlayerPoints>
                      {player.bonusPoints > 0 && (
                        <BonusPointsInline>(+{player.bonusPoints})</BonusPointsInline>
                      )}
                    </PlayerPointsWrapper>
                  </PlayerItem>
                ))}
              </PlayersList>
            </TeamCard>
          ))}
        </TeamsGrid>
      </TeamsSection>

      <StatisticsSection>
        <SectionTitleRow>
          <SectionTitle>Player Statistics</SectionTitle>
          <InfoButton
            title="Player Statistics"
            sections={[
              {
                title: 'About Statistics',
                description: 'This table shows detailed statistics for each player who participated in the mission.'
              },
              {
                title: 'Statistics Fields',
                fields: [
                  { name: 'Player', description: 'The player\'s username. Click to view their profile.' },
                  { name: 'Clan', description: 'The player\'s clan affiliation, if any' },
                  { name: 'Faction', description: 'Which team the player was on - Humans or Aliens' },
                  { name: 'Result', description: 'Whether the player\'s team won or lost' },
                  { name: 'Points', description: 'Base points earned from this mission (win/lose/draw)' },
                  { name: 'Bonus', description: 'Additional bonus points earned for completing objectives' },
                  { name: 'Total Games', description: 'Total number of games this player has participated in' },
                  { name: 'Win Rate', description: 'The player\'s overall win percentage across all games' }
                ]
              }
            ]}
          />
        </SectionTitleRow>
        <StatsTable>
          <StatsHeader>
            <StatsHeaderCell>Player</StatsHeaderCell>
            <StatsHeaderCell>Clan</StatsHeaderCell>
            <StatsHeaderCell>Faction</StatsHeaderCell>
            <StatsHeaderCell>Result</StatsHeaderCell>
            <StatsHeaderCell>Points</StatsHeaderCell>
            <StatsHeaderCell>Bonus</StatsHeaderCell>
            <StatsHeaderCell>Total Games</StatsHeaderCell>
            <StatsHeaderCell>Win Rate</StatsHeaderCell>
          </StatsHeader>
          <StatsBody>
            {gameDetail.allPlayers.map((player, index) => (
              <StatsRow
                key={player.userId || `unknown-stats-${index}`}
                onClick={player.userId ? () => navigate(`/profile/${player.userId}`) : undefined}
                isClickable={!!player.userId}
                $faction={player.faction}
              >
                <StatsCell>{player.username}</StatsCell>
                <StatsCell>
                  {player.clanName ? (
                    <ClanBadge>{player.clanName}</ClanBadge>
                  ) : (
                    <NoClanText>No Clan</NoClanText>
                  )}
                </StatsCell>
                <StatsCell>
                  <FactionBadge faction={player.faction}>
                    {player.faction === 'human' ? 'Human' : 'Alien'}
                  </FactionBadge>
                </StatsCell>
                <StatsCell>
                  <ResultBadge result={player.isWinner ? 'victory' : 'defeat'}>
                    {player.isWinner ? 'Victory' : 'Defeat'}
                  </ResultBadge>
                </StatsCell>
                <StatsCell>+{player.pointsEarned}</StatsCell>
                <BonusStatsCell $hasBonus={player.bonusPoints > 0}>
                  {player.bonusPoints > 0 ? `+${player.bonusPoints}` : '-'}
                </BonusStatsCell>
                <StatsCell>{player.totalGames || 0}</StatsCell>
                <StatsCell>
                  <WinRateText rate={player.winRate || 0}>
                    {(player.winRate || 0).toFixed(1)}%
                  </WinRateText>
                </StatsCell>
              </StatsRow>
            ))}
          </StatsBody>
        </StatsTable>
      </StatisticsSection>

      {/* Mission Map Section */}
      {gameDetail.mapData && (
        <MapSection>
          <SectionTitleRow>
            <SectionTitle>Mission Map</SectionTitle>
            <InfoButton
              title="Mission Map"
              sections={[
                {
                  title: 'About Mission Map',
                  description: 'Interactive map showing the field layout and all device locations during the mission.'
                },
                {
                  title: 'Map Controls',
                  fields: [
                    { name: 'Zoom', description: 'Use mouse wheel or pinch gesture to zoom in/out' },
                    { name: 'Pan', description: 'Click and drag to move around the map' },
                    { name: 'Labels Toggle (T)', description: 'Show or hide device name labels on the map' },
                    { name: 'Fullscreen', description: 'Click the expand button to view the map in fullscreen mode' }
                  ]
                },
                {
                  title: 'Device Markers',
                  description: 'Colored circles represent different devices on the field. Hover over a device to see its name and current status.'
                }
              ]}
            />
          </SectionTitleRow>
          <MissionMapViewer
            mapData={gameDetail.mapData}
            devices={gameDetail.devices}
            height="450px"
            fieldId={gameDetail.fieldId}
            fieldName={gameDetail.fieldName}
            operationTemplateName={gameDetail.fmotName}
          />
        </MapSection>
      )}

      {/* Completed Objectives Section */}
      {gameDetail.completedObjectives && gameDetail.completedObjectives.length > 0 && (
        <ObjectivesSection>
          <SectionTitleRow>
            <SectionTitle>Completed Objectives</SectionTitle>
            <InfoButton
              title="Completed Objectives"
              sections={[
                {
                  title: 'About Objectives',
                  description: 'This section lists all objectives that were completed during the mission by both teams.'
                },
                {
                  title: 'Objective Details',
                  fields: [
                    { name: 'Objective Name', description: 'The name and description of the completed objective' },
                    { name: 'Device', description: 'The device where the objective was completed' },
                    { name: 'Completed By', description: 'The player who completed the objective' },
                    { name: 'Faction', description: 'Which team (Human or Alien) completed the objective' }
                  ]
                }
              ]}
            />
          </SectionTitleRow>
          <CompletedObjectivesList
            objectives={gameDetail.completedObjectives}
            players={gameDetail.allPlayers}
            allObjectives={gameDetail.allObjectives}
          />
        </ObjectivesSection>
      )}

      {/* Uncompleted Objectives Section */}
      {gameDetail.allObjectives && gameDetail.allObjectives.length > 0 && (
        <ObjectivesSection>
          <SectionTitleRow>
            <SectionTitle>Uncompleted Objectives</SectionTitle>
            <InfoButton
              title="Uncompleted Objectives"
              sections={[
                {
                  title: 'About Uncompleted Objectives',
                  description: 'This section lists all objectives that were NOT completed during the mission.'
                },
                {
                  title: 'Objective Details',
                  fields: [
                    { name: 'Objective Name', description: 'The name of the objective that was not completed' },
                    { name: 'Type', description: 'The type of objective - Count, Time, or Points' },
                    { name: 'Faction', description: 'Which team (Human or Alien) was supposed to complete this objective' },
                    { name: 'Critical', description: 'If marked as Critical, completing this objective would win the game' }
                  ]
                }
              ]}
            />
          </SectionTitleRow>
          <UncompletedObjectivesList
            allObjectives={gameDetail.allObjectives}
            completedObjectives={gameDetail.completedObjectives || []}
          />
        </ObjectivesSection>
      )}

      {/* Mission Timeline Section */}
      {gameDetail.deviceEvents && gameDetail.deviceEvents.length > 0 && (
        <TimelineSection>
          <SectionTitleRow>
            <SectionTitle>Mission Timeline</SectionTitle>
            <InfoButton
              title="Mission Timeline"
              sections={[
                {
                  title: 'About Timeline',
                  description: 'Chronological list of all events that occurred during the mission, from start to finish.'
                },
                {
                  title: 'Event Details',
                  fields: [
                    { name: 'Time', description: 'When the event occurred during the mission' },
                    { name: 'Event Description', description: 'What happened - objective completed, device activated, etc.' },
                    { name: 'Device', description: 'The device where the event took place' },
                    { name: 'Player', description: 'The player who triggered the event' },
                    { name: 'Faction', description: 'Which team (Humans or Aliens) the player belongs to' }
                  ]
                }
              ]}
            />
          </SectionTitleRow>
          <DeviceEventTimeline
            events={gameDetail.deviceEvents}
            players={gameDetail.allPlayers}
          />
        </TimelineSection>
      )}

      {/* Reviews Section */}
      <ReviewsSection>
        <ReviewsSectionTitle>Reviews</ReviewsSectionTitle>
        
        <ReviewsContent>
          {/* Review Summary with Write Review - видят все */}
          {reviewSummary && (
            <ReviewSummary
              summary={reviewSummary}
              loading={reviewsLoading}
              targetType="game"
              targetId={gameId || ''}
              showWriteReview={true}
              canWriteReview={!!user}
              onWriteReview={() => setIsReviewModalOpen(true)}
              writeReviewText={myReview ? 'Edit My Review' : 'Write Review'}
              writeReviewMessage={
                field
                  ? 'Fields cannot write reviews'
                  : referee
                    ? 'Referees cannot write reviews'
                    : !user
                      ? 'Login to write a review'
                      : undefined
              }
            />
          )}


        </ReviewsContent>
      </ReviewsSection>

      {/* Review Modal */}
      {gameDetail && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          targetType="game"
          targetId={gameId!}
          targetName={`${gameDetail.gameType.toUpperCase()} Game - ${gameDetail.operationId}`}
          onReviewSubmitted={handleReviewSubmitted}
          existingReview={myReview}
        />
      )}

        </Content>
      </Container>
    </Background>
  );
};

// Styled Components
const Background = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors?.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  padding-top: 100px;
  font-family: 'Orbitron', sans-serif;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 968px) {
    max-width: 95%;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #00d4ff;
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #ff4444;
  font-size: 18px;
  background: var(--legacy-card-bg);
  border: 1px solid #ff4444;
  border-radius: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const BackButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  color: #00d4ff;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  font-family: 'Orbitron', sans-serif;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
  }
`;

const GameTitle = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #00d4ff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const GameInfo = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const InfoSection = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: none;
`;

const PointsSection = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: none;
`;

const InfoTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #00d4ff;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8bb8d8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #fff;
  font-weight: 500;
`;

const ClickableInfoValue = styled.span`
  font-size: 14px;
  color: #00d4ff;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #fff;
    text-shadow: 0 0 8px #00d4ff;
  }
`;

const NotesSection = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NotesText = styled.p`
  margin: 0;
  padding: 12px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  line-height: 1.5;
`;

const PointsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PointsItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
`;

const PointsLabel = styled.span`
  font-size: 14px;
  color: #8bb8d8;
`;

const PointsValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #00d4ff;
`;

const TeamsSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #00d4ff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div<{ isWinner: boolean }>`
  background: var(--legacy-card-bg);
  border: 1px solid ${props => props.isWinner ? '#4CAF50' : '#F44336'};
  border-radius: 12px;
  padding: 18px;
  backdrop-filter: none;
  border-left: 4px solid ${props => props.isWinner ? '#4CAF50' : '#F44336'};
`;

const TeamHeader = styled.div<{ faction: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const FactionBadge = styled.span<{ faction: string }>`
  background: ${props => getFactionColor(props.faction)};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const TeamResult = styled.span<{ isWinner: boolean }>`
  color: ${props => props.isWinner ? '#4CAF50' : '#F44336'};
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
`;

const TeamStats = styled.div`
  color: #8bb8d8;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TeamLeader = styled.div<{ isClickable?: boolean }>`
  color: #00d4ff;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  transition: all 0.3s ease;
  opacity: ${props => props.isClickable ? 1 : 0.7};
  
  &:hover {
    ${props => props.isClickable && `
      color: #fff;
      text-shadow: 0 0 8px #00d4ff;
    `}
  }
`;

const PlayersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PlayerItem = styled.div<{ isClickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  transition: all 0.3s ease;
  opacity: ${props => props.isClickable ? 1 : 0.7};
  
  &:hover {
    ${props => props.isClickable && `
      background: rgba(0, 212, 255, 0.2);
      border-color: #00d4ff;
      transform: translateY(-1px);
    `}
  }
`;

const PlayerName = styled.span`
  font-size: 14px;
  color: #fff;
`;

const PlayerPointsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PlayerPoints = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #4CAF50;
`;

const BonusPointsInline = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #ffd700;
`;

const StatisticsSection = styled.div`
  margin-bottom: 30px;
`;

const StatsTable = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: none;
`;

const StatsHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 0.8fr 0.8fr 1fr 1fr;
  background: rgba(0, 212, 255, 0.1);
  border-bottom: 1px solid #00d4ff;
`;

const StatsHeaderCell = styled.div`
  padding: 12px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatsBody = styled.div``;

const StatsRow = styled.div<{ isClickable?: boolean; $faction?: string }>`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 0.8fr 0.8fr 1fr 1fr;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  transition: all 0.3s ease;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  opacity: ${props => props.isClickable ? 1 : 0.7};
  background: ${props => {
    const f = props.$faction?.toLowerCase();
    if (f === 'alien' || f === 'aliens') return 'rgba(255, 136, 0, 0.08)';
    if (f === 'human' || f === 'humans') return 'rgba(59, 130, 246, 0.08)';
    return 'transparent';
  }};
  border-left: 3px solid ${props => {
    const f = props.$faction?.toLowerCase();
    if (f === 'alien' || f === 'aliens') return '#ff8800';
    if (f === 'human' || f === 'humans') return '#3b82f6';
    return 'transparent';
  }};

  &:hover {
    ${props => props.isClickable && `
      background: ${props.$faction?.toLowerCase() === 'alien' || props.$faction?.toLowerCase() === 'aliens'
        ? 'rgba(255, 136, 0, 0.15)'
        : 'rgba(59, 130, 246, 0.15)'};
      transform: translateX(4px);
    `}
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StatsCell = styled.div`
  padding: 12px 8px;
  font-size: 13px;
  color: #fff;
  display: flex;
  align-items: center;
`;

const BonusStatsCell = styled.div<{ $hasBonus: boolean }>`
  padding: 12px 8px;
  font-size: 13px;
  color: ${props => props.$hasBonus ? '#ffd700' : '#666'};
  font-weight: ${props => props.$hasBonus ? '600' : '400'};
  display: flex;
  align-items: center;
`;

const ClanBadge = styled.span`
  background: rgba(139, 184, 216, 0.2);
  color: #8bb8d8;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const NoClanText = styled.span`
  color: #666;
  font-size: 11px;
  font-style: italic;
`;

const WinRateText = styled.span<{ rate: number }>`
  color: ${props => 
    props.rate >= 70 ? '#4CAF50' : 
    props.rate >= 50 ? '#FF9800' : 
    '#F44336'
  };
  font-weight: 600;
`;

const ResultBadge = styled.span<{ result: string }>`
  background: ${props => getResultColor(props.result)};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

// Map, Objectives, Timeline Section Styles
const MapSection = styled.div`
  margin-bottom: 30px;
`;

const ObjectivesSection = styled.div`
  margin-bottom: 30px;
`;

const TimelineSection = styled.div`
  margin-bottom: 30px;
`;

// Reviews Section Styles
const ReviewsSection = styled.div`
  margin-top: 30px;
`;

const ReviewsSectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #00d4ff;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ReviewsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;


// Utility functions
const getResultColor = (result: string) => {
  switch (result.toLowerCase()) {
    case 'victory': return '#22c55e';
    case 'defeat': return '#ef4444';
    case 'draw': return '#f59e0b';
    default: return '#6b7280';
  }
};

const getFactionColor = (faction: string) => {
  switch (faction.toLowerCase()) {
    case 'human': return '#3b82f6';
    case 'alien': return '#ff8800';
    default: return '#6b7280';
  }
};

export default GameDetailPage;
